import { isEmpty } from 'lodash'
import { DataItem, load } from 'scrivito'

import {
  getDocumentScope,
  getRelationFilter,
} from '@/Components/Document/documentUtils'
import {
  DocumentRelationType,
  DocumentsFilters,
} from '@/Components/Document/types'
import { Document } from '@/Data/Document/DocumentDataClass'
import { DocumentDocumentRelation } from '@/Data/DocumentDocumentRelation/DocumentDocumentRelationDataClass'
import { DataScopeOrder } from '@/Data/types'

interface UseDocumentLoaderProps {
  entityId: string | null
  limit: number
  filters: DocumentsFilters
  order: DataScopeOrder
  search: string
  relationTo: DocumentRelationType | undefined
  setDocuments: React.Dispatch<React.SetStateAction<DataItem[]>>
  setCount: React.Dispatch<React.SetStateAction<number>>
  setError: React.Dispatch<React.SetStateAction<unknown>>
}

/**
 * Hook for loading document data related to an entity (e.g., contact, project, activity).
 *
 * @internal Do not use this hook directly. Use the DocumentContextProvider and
 * its exported context instead.
 *
 */
export const useDocumentLoader = ({
  entityId,
  relationTo,
  order,
  filters,
  search,
  limit,
  setError,
  setCount,
  setDocuments,
}: UseDocumentLoaderProps) => {
  // Loads documents either from contact or folder
  const loadDocumentsFromRelation = (ids: string[]) => {
    if (isEmpty(ids)) {
      setDocuments([])
      return
    }
    const result = Document.all()
      .transform({
        filters: {
          ids: ids.join(','),
        },
        order: [order],
      })
      .take()

    setDocuments(result)
  }

  const loadDocumentsForEntity = () => {
    if (!entityId || !relationTo) return

    load(() => {
      try {
        const scope = getDocumentScope(relationTo)
        if (!scope) return

        const filterKey = getRelationFilter(relationTo)
        const dataFilters = {
          [filterKey]: entityId,
          document_type: filters.type.value,
          document_search: search,
        }
        const transformedScope = scope.transform({
          // contact, project, activity
          filters: dataFilters,
          order: [order],
          limit: limit,
        })
        const ids = transformedScope
          .take()
          .map((e) => e.get('document') as string)

        loadDocumentsFromRelation(ids)
        setCount(transformedScope.count() || 0)
      } catch (e) {
        setError(e)
      }
    })
  }
  const loadDocumentsForFolder = (
    folderId: string,
  ): Promise<DataItem[] | undefined> => {
    return load(() => {
      try {
        const scope = DocumentDocumentRelation.all()
        const transform = {
          filters: {
            father: folderId,
          },
          limit,
        }
        const relations = scope.transform(transform).take()
        const relationIds = relations
          .map((e) => e.get('child') as string)
          .filter(Boolean)

        if (isEmpty(relationIds)) return []
        return Document.all()
          .transform({
            filters: {
              ids: relationIds.join(','),
            },
            limit,
          })
          .take()
      } catch (e) {
        setError(e)
      }
    })
  }

  return {
    loadDocumentsFromRelation,
    loadDocumentsForEntity,
    loadDocumentsForFolder,
  }
}
